import {
  Button,
  Card,
  FormGroup,
  NumericInput,
  Switch,
} from "@blueprintjs/core";
import { ItemSelect, MediaImage } from "@package/ui-apollo-components";
import {
  GetPostListNameIdDocument,
  useGetMediaQuery,
  type GetPostListNameIdQuery,
  type GetPostListNameIdQueryVariables,
  type ProfileModel,
  type ProfileRestingModelEntry,
} from "@package/ui-gql/index-data-access";
import { getMediaType } from "@package/ui-wall-engine";
import { type WritableDraft } from "immer";
import { MediaTagSelector } from "../../../MediaTagSelector/MediaTagSelector";
import { PostCreateDrawer } from "../post/PostEditDrawer";

type ProfileRestingMediaListEditorEntryProps = {
  disabled: boolean;
  resting: ProfileRestingModelEntry;
  onRestingMediaUpdated: (resting: ProfileRestingModelEntry) => void;
  onRestingMediaDeleted: () => void;
};

function ProfileRestingMediaListEditorEntry({
  disabled = false,
  resting,
  onRestingMediaUpdated,
  onRestingMediaDeleted,
}: ProfileRestingMediaListEditorEntryProps) {
  const { data: dataMedia } = useGetMediaQuery({
    variables: { id: resting.media ?? "" },
    skip: !resting.media,
  });

  return (
    <Card>
      <FormGroup label="Content">
        <ItemSelect<GetPostListNameIdQuery, GetPostListNameIdQueryVariables>
          activeItemId={resting.post}
          createDrawerElement={PostCreateDrawer}
          disabled={disabled}
          document={GetPostListNameIdDocument}
          nullable
          onItemSelected={(id) =>
            onRestingMediaUpdated({
              ...resting,
              post: id,
            })
          }
        />
      </FormGroup>
      <FormGroup>
        <Switch
          disabled={disabled}
          label="Background Transparency"
          checked={!!resting.alphaOn}
          onChange={() =>
            onRestingMediaUpdated({
              ...resting,
              alphaOn: !resting.alphaOn,
            })
          }
        />
      </FormGroup>
      <FormGroup>
        <Switch
          disabled={disabled}
          label="Can Interrupt"
          checked={!!resting.canInterrupt}
          onChange={() =>
            onRestingMediaUpdated({
              ...resting,
              canInterrupt: !resting.canInterrupt,
            })
          }
        />
      </FormGroup>
      <FormGroup>
        <Switch
          disabled={disabled}
          label="Play Audio"
          checked={!!resting.playAudio}
          onChange={() =>
            onRestingMediaUpdated({
              ...resting,
              playAudio: !resting.playAudio,
            })
          }
        />
      </FormGroup>
      <FormGroup
        disabled={disabled}
        label="Media File"
        labelFor="control-media"
      >
        <div className="mb-2">
          <MediaTagSelector
            fill
            large
            minimal
            disabled={disabled}
            id={resting.media ?? undefined}
            onRemoveMedia={() => {
              onRestingMediaUpdated({
                ...resting,
                media: null,
              });
            }}
            onSelectMedia={(mediaId) => {
              onRestingMediaUpdated({
                ...resting,
                media: mediaId,
              });
            }}
          />
        </div>
        <MediaImage id={resting.media ?? undefined} />
      </FormGroup>
      {!!resting.media &&
        (!dataMedia?.item || getMediaType(dataMedia.item) === "image") && (
          <FormGroup
            disabled={disabled}
            fill
            label="Image Show Duration"
            helperText="The amount of time in seconds to display the Image. Videos play until completion."
          >
            <NumericInput
              min={0}
              allowNumericCharactersOnly
              fill
              value={resting.imageSettings?.showTime ?? 0}
              onValueChange={(value) => {
                onRestingMediaUpdated({
                  ...resting,
                  imageSettings: {
                    ...resting.imageSettings,
                    showTime: value,
                  },
                });
              }}
            />
          </FormGroup>
        )}
      <div className="flex flex-row justify-end">
        <Button
          disabled={disabled}
          minimal
          intent="danger"
          text="Remove Resting Media"
          onClick={() => onRestingMediaDeleted()}
        />
      </div>
    </Card>
  );
}

type ProfileRestingMediaListEditorProps = {
  disabled?: boolean;
  profile?: ProfileModel;
  onProfileChanged: (
    callback: (draft: WritableDraft<ProfileModel>) => void,
  ) => void;
};

export function ProfileRestingMediaListEditor({
  disabled = false,
  profile,
  onProfileChanged,
}: ProfileRestingMediaListEditorProps) {
  return (
    <>
      <FormGroup
        disabled={disabled}
        label="No Interaction Timeout"
        helperText={"Seconds of inactivity before playing resting state video"}
      >
        <NumericInput
          min={0}
          allowNumericCharactersOnly
          fill
          value={profile?.resting?.staleStateTimeout ?? 0}
          onValueChange={(value) => {
            onProfileChanged((draft) => {
              draft.resting = draft.resting ?? {};
              draft.resting.staleStateTimeout = value;
            });
          }}
        />
      </FormGroup>
      <h6 className="bp-heading mb-4">Resting State Media</h6>
      {profile?.resting?.items?.map((resting, index) => (
        <div key={index} className="mb-4">
          <ProfileRestingMediaListEditorEntry
            disabled={disabled}
            resting={resting}
            onRestingMediaUpdated={(r) => {
              onProfileChanged((draft) => {
                draft.resting = draft.resting ?? {};
                draft.resting.items = draft.resting.items ?? [];
                draft.resting.items[index] = r;
              });
            }}
            onRestingMediaDeleted={() => {
              onProfileChanged((draft) => {
                draft.resting = draft.resting ?? {};
                draft.resting.items = draft.resting.items ?? [];
                draft.resting.items.splice(index, 1);
              });
            }}
          />
        </div>
      ))}
      <Button
        icon="add"
        intent="primary"
        minimal
        text="Add Resting Media"
        onClick={() => {
          onProfileChanged((draft) => {
            draft.resting = draft.resting ?? {};
            draft.resting.items = draft.resting.items ?? [];
            draft.resting.items.push({
              alphaOn: false,
              canInterrupt: false,
              imageSettings: {
                showTime: 0,
              },
              playAudio: false,
            });
          });
        }}
      />
    </>
  );
}
